import React from 'react';
import { useSocket } from '../contexts/SocketContext';
import { pinNativeTopLayer } from '../utils/visualViewportPin';

/**
 * Tells the player what is happening when the game is not reachable.
 *
 * Without this a dropped connection looks identical to a frozen game: the
 * multiplier stops, nothing responds, and the natural conclusion is that the app
 * ate the bet. Naming the state — and saying that a placed wager is safe —
 * is the difference between "it's reconnecting" and "it stole my money".
 */
const MESSAGES = {
    reconnecting: {
        title: 'Reconnecting…',
        detail: 'Your connection dropped. Any wager already placed is still running on the server.',
        tone: 'rgba(234,179,8,0.95)',
    },
    'server-restarting': {
        title: 'Server updating',
        detail: 'The current round will finish, then the game returns in a moment.',
        tone: 'rgba(59,130,246,0.95)',
    },
} as const;

const ConnectionBanner: React.FC = () => {
    const { connectionState } = useSocket();
    if (connectionState === 'online') return null;

    const message = MESSAGES[connectionState];
    if (!message) return null;

    // Layout lives in index.css (.connection-banner) so the top padding can
    // clear the iPhone status bar and Dynamic Island with env(safe-area-inset-top).
    // In the app it also follows the visual viewport, so a keyboard pan cannot
    // move it off screen (utils/visualViewportPin).
    return (
        <div
            ref={pinNativeTopLayer}
            role="status"
            aria-live="polite"
            className="connection-banner"
            data-state={connectionState}
            style={{ background: message.tone }}
        >
            <span>{message.title}</span>
            <span className="connection-banner-detail">{message.detail}</span>
        </div>
    );
};

export default ConnectionBanner;
